/* =========================================================================
   WMP Info Bubble — Tooltip / popover positioned near trigger element
   ========================================================================= */
(function () {
  'use strict';

  var bubble = null;
  var arrow = null;
  var titleEl = null;
  var bodyEl = null;
  var announcer = null;
  var announceTimer = null;
  var built = false;
  var currentTrigger = null;
  var BUBBLE_ID = 'wmp-info-bubble';
  var CONTENT_ID = 'wmp-info-bubble-content';
  var ANNOUNCER_ID = 'wmp-info-bubble-announcer';

  /* -----------------------------------------------------------------------
     Build DOM (once, reused)
     ----------------------------------------------------------------------- */
  function build() {
    if (built) return;

    var root = document.getElementById('wmp-prescription-flow');
    if (!root) return;

    bubble = document.createElement('div');
    bubble.className = 'wmp-info-bubble';
    bubble.id = BUBBLE_ID;
    bubble.setAttribute('role', 'tooltip');
    bubble.setAttribute('aria-hidden', 'true');

    arrow = document.createElement('div');
    arrow.className = 'wmp-info-bubble__arrow';
    bubble.appendChild(arrow);

    var inner = document.createElement('div');
    inner.className = 'wmp-info-bubble__inner';
    inner.id = CONTENT_ID;

    titleEl = document.createElement('div');
    titleEl.className = 'wmp-info-bubble__title';
    inner.appendChild(titleEl);

    bodyEl = document.createElement('div');
    bodyEl.className = 'wmp-info-bubble__body';
    inner.appendChild(bodyEl);

    bubble.appendChild(inner);
    root.appendChild(bubble);

    announcer = document.createElement('div');
    announcer.id = ANNOUNCER_ID;
    announcer.className = 'wmp-sr-only';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    root.appendChild(announcer);

    built = true;
  }

  /* -----------------------------------------------------------------------
     Position bubble relative to trigger
     ----------------------------------------------------------------------- */
  function position(triggerEl) {
    if (!bubble || !triggerEl) return;

    var root = document.getElementById('wmp-prescription-flow');
    if (!root) return;

    var triggerRect = triggerEl.getBoundingClientRect();
    var rootRect = root.getBoundingClientRect();

    // Bubble dimensions (need to make visible briefly to measure)
    bubble.style.visibility = 'hidden';
    bubble.style.display = 'block';
    var bubbleRect = bubble.getBoundingClientRect();
    bubble.style.visibility = '';

    var gap = 8; // space between trigger and bubble

    // Center horizontally on trigger
    var left = (triggerRect.left - rootRect.left) + (triggerRect.width / 2) - (bubbleRect.width / 2);

    // Clamp to root bounds
    var minLeft = 8;
    var maxLeft = rootRect.width - bubbleRect.width - 8;
    left = Math.max(minLeft, Math.min(maxLeft, left));

    // Try above first
    var top = (triggerRect.top - rootRect.top) - bubbleRect.height - gap;
    var flipped = false;

    if (top < 0) {
      // Flip below
      top = (triggerRect.bottom - rootRect.top) + gap;
      flipped = true;
    }

     // Try below first (default)
     var top = (triggerRect.bottom - rootRect.top) + gap;
     var flipped = false;

     // Check if it fits below; if not, flip above
     if (top + bubbleRect.height > rootRect.height) {
       // Flip above
       top = (triggerRect.top - rootRect.top) - bubbleRect.height - gap;
       flipped = true;
     }

     bubble.style.left = left + 'px';
     bubble.style.top = top + 'px';

     // Arrow position
     var arrowLeft = (triggerRect.left - rootRect.left) + (triggerRect.width / 2) - left - 6; // 6 = half arrow width
     arrowLeft = Math.max(12, Math.min(bubbleRect.width - 24, arrowLeft));
     arrow.style.left = arrowLeft + 'px';

     if (flipped) {
       bubble.classList.add('wmp-info-bubble--above');
       bubble.classList.remove('wmp-info-bubble--below');
     } else {
       bubble.classList.add('wmp-info-bubble--below');
       bubble.classList.remove('wmp-info-bubble--above');
     }
  }

  /* -----------------------------------------------------------------------
     Outside click / ESC handlers
     ----------------------------------------------------------------------- */
  function onDocClick(e) {
    if (!bubble) return;
    if (bubble.contains(e.target)) return;
    if (currentTrigger && currentTrigger.contains(e.target)) return;
    hide();
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') hide();
  }

  function onScroll() {
    hide();
  }

  function bindDismiss() {
    // Delay to avoid catching the triggering click
    setTimeout(function () {
      document.addEventListener('click', onDocClick, true);
      document.addEventListener('keydown', onKeyDown);
      window.addEventListener('scroll', onScroll, true);
    }, 0);
  }

  function unbindDismiss() {
    document.removeEventListener('click', onDocClick, true);
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('scroll', onScroll, true);
  }

  function setTriggerState(triggerEl, expanded) {
    if (!triggerEl || typeof triggerEl.setAttribute !== 'function') return;

    if (triggerEl.hasAttribute('aria-expanded')) {
      triggerEl.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    if (expanded) {
      triggerEl.setAttribute('aria-describedby', CONTENT_ID);
      triggerEl.setAttribute('aria-controls', BUBBLE_ID);
    } else {
      triggerEl.removeAttribute('aria-describedby');
      triggerEl.removeAttribute('aria-controls');
    }
  }

  function getAnnouncementText() {
    if (!titleEl || !bodyEl) return '';

    return (titleEl.textContent + ' ' + bodyEl.textContent)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function announce() {
    if (!announcer) return;

    if (announceTimer) {
      window.clearTimeout(announceTimer);
      announceTimer = null;
    }

    var text = getAnnouncementText();
    announcer.textContent = '';
    if (!text) return;

    announceTimer = window.setTimeout(function () {
      announcer.textContent = text;
      announceTimer = null;
    }, 50);
  }

  function clearAnnouncement() {
    if (announceTimer) {
      window.clearTimeout(announceTimer);
      announceTimer = null;
    }

    if (announcer) announcer.textContent = '';
  }

  /* -----------------------------------------------------------------------
     show(triggerEl, { title?, content })
     ----------------------------------------------------------------------- */
  function show(triggerEl, opts) {
    opts = opts || {};
    var isSameVisible = currentTrigger === triggerEl && bubble && bubble.classList.contains('wmp-info-bubble--visible');

    // If clicking the same trigger, toggle off
    if (opts.toggle !== false && isSameVisible) {
      hide();
      return;
    }

    build();
    if (!bubble) return;

    // Hide any existing bubble first
    if (bubble.classList.contains('wmp-info-bubble--visible')) {
      unbindDismiss();
      bubble.classList.remove('wmp-info-bubble--visible');
      bubble.setAttribute('aria-hidden', 'true');
      setTriggerState(currentTrigger, false);
    }

    currentTrigger = triggerEl;

    // Title
    if (opts.title) {
      titleEl.textContent = opts.title;
      titleEl.style.display = '';
    } else {
      titleEl.textContent = '';
      titleEl.style.display = 'none';
    }

    // Body
    if (typeof opts.content === 'string') {
      bodyEl.innerHTML = opts.content;
    } else {
      bodyEl.textContent = opts.content || '';
    }

    position(triggerEl);
    bubble.classList.add('wmp-info-bubble--visible');
    bubble.setAttribute('aria-hidden', 'false');
    setTriggerState(triggerEl, true);
    if (opts.announce !== false && !isSameVisible && document.activeElement !== triggerEl) announce();
    bindDismiss();
  }

  /* -----------------------------------------------------------------------
     hide()
     ----------------------------------------------------------------------- */
  function hide() {
    if (!bubble) return;
    bubble.classList.remove('wmp-info-bubble--visible');
    bubble.setAttribute('aria-hidden', 'true');
    setTriggerState(currentTrigger, false);
    clearAnnouncement();
    unbindDismiss();
    currentTrigger = null;
  }

  /* -----------------------------------------------------------------------
     Public API
     ----------------------------------------------------------------------- */
  window.WmpInfoBubble = {
    show: show,
    hide: hide
  };
})();
